<template>
  <v-container fluid class='pa-0 pb-8'>
    <v-container fluid>
      <Breadcrumbs :title="$t('modules.portalalumno.payments.cardRegistration.breadcrumbs.title')"
        :description="$t('modules.portalalumno.payments.cardRegistration.breadcrumbs.description')"></Breadcrumbs>
      <v-row justify='center'>
        <v-col class='viewSpaces' cols='12'  md='8' lg='6'>
          <v-card class='pa-2 pa-md-6'>
            <v-card-text>
              <div v-if='loading && !formUrl' class='text-center py-6'>
                <v-progress-circular indeterminate color='primary'></v-progress-circular>
                <div class='mt-2'>{{ $t('modules.portalalumno.payments.cardRegistration.view.loading') }}</div>
              </div>
              <div v-else-if='error' class='error-message mt-4'>
                <v-alert type='error'>{{ error }}</v-alert>
              </div>
              <div v-else-if='success' class='success-message mt-4'>
                <v-alert type='success'>{{ $t('modules.portalalumno.payments.cardRegistration.view.successMessage') }}</v-alert>
                <v-btn color='primary' class='mt-4' @click='resetForm'>
                  {{ $t('modules.portalalumno.payments.cardRegistration.view.registerAnotherCard') }}
                </v-btn>
              </div>
              <div v-else>
                <div v-if='studentRegisters && studentRegisters.length > 0' class='mt-4'>
                  <div class='mb-2 font-semibold text-blue-900 text-base'>
                    {{ $t('modules.portalalumno.payments.cardRegistration.view.selectCareerQuestion') }}
                  </div>
                  <v-select
                    v-model='selectedRegister'
                    :items='studentRegisters'
                    :item-text='studentCareerDisplayField'
                    :item-value='studentCareerValueField'
                    @change='handleStudentCareerUpdate'
                    hide-details
                    solo
                    class="border-b border-gray-300 rounded-none shadow-none bg-transparent px-0 py-2 text-base font-semibold focus:ring-0 focus:border-blue-700 transition-colors duration-200"
                  ></v-select>
                </div>
                <div v-else-if='!loading && (!studentRegisters || studentRegisters.length === 0)' class='mt-4'>
                  <v-alert type='info' dense class='mb-4'>
                    {{ $t('modules.portalalumno.payments.cardRegistration.view.noCareerFound') }}
                  </v-alert>
                </div>
                <CardRegistrationActions
                  :cardData="cardData"
                  :processingRequest="processingRequest"
                  :registerButtonTitle="getRegisterButtonText()"
                  @register="registerCard"
                  @cancel="showCancelDialog = true"
                  class="mt-4"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog
      v-model="showIframeModal"
      persistent
      scrollable
      :fullscreen="$vuetify.breakpoint.mobile"
      max-width="70vw"
      width="100%"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('modules.portalalumno.payments.cardRegistration.view.cardRegistrationModalTitle') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeIframeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-6 grey lighten-5">
          <div v-if='formUrl' class='flywire-form-container'>
            <iframe
              :src='formUrl'
              width='100%'
              height='100%'
              frameborder='0'
              @load='formLoaded = true'
              :style="iframeStyle"
            ></iframe>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCancelDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">{{ $t('modules.portalalumno.payments.cardRegistration.view.cancelSubscriptionTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('modules.portalalumno.payments.cardRegistration.view.cancelSubscriptionConfirmation') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showCancelDialog = false">{{ $t('modules.portalalumno.payments.cardRegistration.view.cancelButton') }}</v-btn>
          <v-btn
            color="red darken-1"
            text
            @click="cancelSubscription"
            :loading="cancellingSubscription"
          >
            {{ $t('modules.portalalumno.payments.cardRegistration.view.confirmButton') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showStatusDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">
          <v-icon :color="statusType === 'success' ? 'success' : 'error'" class="mr-2">
            {{ statusType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          {{ statusType === 'success' ? $t('modules.portalalumno.payments.cardRegistration.view.successTitle') : $t('modules.portalalumno.payments.cardRegistration.view.errorTitle') }}
        </v-card-title>
        <v-card-text>
          <p>{{ statusMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeStatusDialog">
            {{ $t('modules.portalalumno.payments.cardRegistration.view.closeButton') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import CardRegistrationActions from '../Components/CardRegistrationActions.vue';
import baseUrl from '../../../../../core/constants/uri';
import { mapGetters } from 'vuex';
import { $student } from '../Services';

export default {
  name: 'CardRegistrationView',
  components: {
    Breadcrumbs,
    CardRegistrationActions
  },
  data() {
    return {
      loading: false,
      error: null,
      success: false,
      processingRequest: false,
      formUrl: null,
      formLoaded: false,
      allowedOrigins: [
        'https://payment-checkout.demo.flywire.com',
        'https://payment-checkout.flywire.com'
      ],
      selectedRegister: null,
      studentCareerDisplayField: 'career',
      studentCareerValueField: 'legajo',
      showCancelDialog: false,
      cancellingSubscription: false,
      cancelSuccess: false,
      showIframeModal: false,
      showStatusDialog: false,
      statusType: 'success',
      statusMessage: '',
      cardData: null,
      loadingCardDetails: false,
    };
  },
  computed: {
    ...mapGetters({
      studentRegisters: 'commons/getStudentRegisters',
    }),
    isCareerSelected() {
      return !!this.selectedRegister;
    },
    iframeStyle() {
      if (this.$vuetify.breakpoint.mobile) {
        return 'min-height: calc(100vh - 96px); height: calc(100vh - 96px);';
      } else if (this.$vuetify.breakpoint.tablet) {
        return 'min-height: calc(80vh - 32px); height: calc(80vh - 32px);';
      } else {
        return 'min-height: calc(70vh - 32px); height: calc(70vh - 32px);';
      }
    }
  },
  watch: {
    studentRegisters(newVal) {
      if (newVal && newVal.length > 0 && !this.selectedRegister) {
        this.selectedRegister = newVal[0][this.studentCareerValueField];
      }
    },
    formUrl(newVal) {
      if (newVal) {
        this.showIframeModal = true;
      }
    },
    selectedRegister(newVal) {
      if (newVal) {
        this.fetchCardDetails();
      } else {
        this.cardData = null;
      }
    }
  },
  methods: {
    handleStudentCareerUpdate(value) {
      this.selectedRegister = value;
    },

    async fetchCardDetails() {
      if (!this.selectedRegister) {
        this.cardData = null;
        return;
      }

      try {
        this.loadingCardDetails = true;
        this.error = null;

        const authToken = localStorage.getItem('access_token') ? JSON.parse(localStorage.getItem('access_token')) : '';

        const response = await fetch(`${baseUrl}/flywire/payments/payment-methods/${this.selectedRegister}/details`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.last_four_digits && data.brand) {
            this.cardData = data;
          } else {
            this.cardData = null;
          }
        } else {
          // If response is not ok (404, etc.), it means no card is associated
          this.cardData = null;
        }
      } catch (error) {
        this.error = this.$t('modules.portalalumno.payments.cardRegistration.view.errorFetchingCardDetails');
        this.cardData = null;
      } finally {
        this.loadingCardDetails = false;
      }
    },

    getRegisterButtonText() {
      return this.cardData
        ? this.$t('modules.portalalumno.payments.cardRegistration.view.updateCardButton')
        : this.$t('modules.portalalumno.payments.cardRegistration.view.registerCardButton');
    },

    async registerCard() {
      if (!this.selectedRegister) {
        this.error = this.$t('modules.portalalumno.payments.cardRegistration.view.selectCareerError');
        this.processingRequest = false;
        return;
      }
      try {
        this.processingRequest = true;
        this.error = null;
        this.formUrl = null;
        this.success = false;

        const authToken = localStorage.getItem('access_token') ? JSON.parse(localStorage.getItem('access_token')) : '';

        const studentData = JSON.parse(localStorage.getItem('alumnoSeleccionado'))?.userData;

        if (!studentData) {
          throw new Error(this.$t('modules.portalalumno.payments.cardRegistration.errorFetchingStudentData'));
        }

        const payload = {
          first_name: studentData.name || '',
          last_name: studentData.lastname || '',
          address: studentData.address || '',
          city: '',
          country: studentData.nationality || '',
          state: '',
          phone: studentData.cellphone || studentData.phone || '',
          email: studentData.email || '',
          zip: '',
          studentCareerId: this.selectedRegister,
          studentId: studentData.id.toString(),
          type: 'tokenization'
        };

        const response = await fetch(`${baseUrl}/flywire/orders/subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || this.$t('modules.portalalumno.payments.cardRegistration.errorCreatingCheckout'));
        }

        const data = await response.json();
        if (data && data.hosted_form && data.hosted_form.url) {
          this.formUrl = data.hosted_form.url;
        } else {
          throw new Error(this.$t('modules.portalalumno.payments.cardRegistration.invalidCheckoutSession'));
        }
      } catch (error) {
        this.error = error.message || this.$t('modules.portalalumno.payments.cardRegistration.errorProcessingCard');
      } finally {
        this.processingRequest = false;
        if (!this.formUrl && !this.success) {
          this.loading = false;
        }
      }
    },
    async cancelSubscription() {
      try {
        this.cancellingSubscription = true;

        const studentData = JSON.parse(localStorage.getItem('alumnoSeleccionado'))?.userData;

        if (!studentData) {
          throw new Error(this.$t('modules.portalalumno.payments.cardRegistration.errorFetchingStudentData'));
        }

        const payload = {
          studentCareerId: this.selectedRegister,
          state: 'INACTIVE',
          email: studentData.email || ''
        };

        await $student.updateSubscriptionStatus(payload);

        this.showStatusDialog = true;
        this.statusType = 'success';
        this.statusMessage = this.$t('modules.portalalumno.payments.cardRegistration.view.subscriptionCancelledMessage');
        this.showCancelDialog = false;

        await this.fetchCardDetails();

        this.$nextTick(() => {
          this.$emit('subscription-cancelled');
          this.$forceUpdate();
        });
      } catch (error) {
        if (error.status === 216) {
          this.statusMessage = this.$t('modules.portalalumno.payments.cardRegistration.view.noActiveSubscription');
        } else if (error.response?.data?.message) {
          this.statusMessage = error.response.data.message;
        } else {
          this.statusMessage = error.message || this.$t('modules.portalalumno.payments.cardRegistration.errorProcessingCard');
        }
        this.showStatusDialog = true;
        this.statusType = 'error';
      } finally {
        this.cancellingSubscription = false;
        this.showCancelDialog = false;
      }
    },
    closeDialog() {
      this.showDialog = false;
        setTimeout(() => {
        this.formUrl = null;
        this.error = null;
        this.formLoaded = false;
      }, 300);
    },


    handleFlywireMessage(event) {
      if (!this.allowedOrigins.includes(event.origin)) {
        return;
      }

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.success === true && data.source === 'checkout_session') {
          this.confirmCardRegistration(data);
        } else if (data.error) {
          this.error = data.error.message || this.$t('modules.portalalumno.payments.cardRegistration.errorProcessingCard');
        }
      } catch (error) {
        this.error = this.$t('modules.portalalumno.payments.cardRegistration.errorProcessingCard');
      }
    },

    async confirmCardRegistration(data) {
      try {
        if (!data.confirm_url || !data.confirm_url.url) {
          throw new Error(this.$t('modules.portalalumno.payments.cardRegistration.invalidCheckoutSession'));
        }

        const authToken = localStorage.getItem('access_token') ? JSON.parse(localStorage.getItem('access_token')) : '';

        const response = await fetch(`${baseUrl}/flywire/orders/confirm-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            url: data.confirm_url.url,
            studentCareerId: this.selectedRegister,
            state: 'ACTIVE'
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || this.$t('modules.portalalumno.payments.cardRegistration.errorConfirmingCard'));
        }

        if (response.status === 200) {
          this.showStatusDialog = true;
          this.statusType = 'success';
          this.statusMessage = this.$t('modules.portalalumno.payments.cardRegistration.view.successMessage');
          this.formUrl = null;
          this.showIframeModal = false;
          await this.fetchCardDetails();
          this.$emit('card-registered');
        }

      } catch (error) {
        this.showStatusDialog = true;
        this.statusType = 'error';
        this.statusMessage = error.message || this.$t('modules.portalalumno.payments.cardRegistration.errorConfirmingCard');
      }
    },

    resetForm() {
      this.success = false;
      this.error = null;
      this.formUrl = null;
      this.formLoaded = false;
      this.showIframeModal = false;
    },

    closeIframeModal() {
      this.showIframeModal = false;
      this.formUrl = null;
      this.error = null;
      this.formLoaded = false;
      this.processingRequest = false;
    },

    closeStatusDialog() {
      this.showStatusDialog = false;
      this.statusMessage = '';
      this.statusType = 'success';
      this.resetForm();
    },

  },
  async mounted() {
    window.addEventListener('message', this.handleFlywireMessage);

    if (this.studentRegisters && this.studentRegisters.length > 0) {
      if (!this.selectedRegister) {
        this.selectedRegister = this.studentRegisters[0][this.studentCareerValueField];
      }
    } else {
      const studentDataFromStorage = JSON.parse(localStorage.getItem('alumnoSeleccionado'));
      if (studentDataFromStorage && studentDataFromStorage.userData && studentDataFromStorage.userData.identification) {
        try {
          await this.$store.dispatch('commons/setStudentData', studentDataFromStorage.userData.identification);
        } catch (dispatchError) {
          this.error = this.$t('modules.portalalumno.payments.cardRegistration.errorFetchingStudentData');
        }
      } else if (!this.studentRegisters || this.studentRegisters.length === 0) {
        this.error = this.$t('modules.portalalumno.payments.cardRegistration.view.noCareerFound');
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleFlywireMessage);
  }
}
</script>

<style scoped>
.flywire-form-container {
  overflow: hidden;
}

.flywire-form-container iframe {
  border: none;
  width: 100%;
  height: 100%;
}

@media (min-width: 1264px) {
  .flywire-form-container {
    min-height: calc(70vh - 32px);
  }
}

@media (min-width: 960px) and (max-width: 1263px) {
  .flywire-form-container {
    min-height: calc(80vh - 32px);
  }
}

@media (max-width: 959px) {
  .flywire-form-container {
    min-height: calc(100vh - 96px);
  }
}

.error-message,
.success-message {
  margin: 20px 0;
}

.button-container {
  margin-top: 1.5rem;
}

.button-row {
  gap: 10px;
}

.button-col {
  padding: 0 5px;
}

@media (max-width: 599px) {
  .button-row {
    gap: 10px;
    flex-direction: column;
  }

  .button-col {
    padding: 5px 0;
  }
}

@media (min-width: 600px) {
  .button-col:first-child {
    padding-right: 5px;
  }

  .button-col:last-child {
    padding-left: 5px;
  }
}
</style>
